import { useMemo } from 'react';

import {
  Box,
  CollapseProps,
  Alert as MantineAlert,
  AlertProps as MantineAlertProps,
  Transition,
  TransitionProps,
} from '@mantine/core';
import { IconWarning } from 'ui-icons';

import { InfoIcon } from '../../Icons';
import { Collapse } from '../../Miscellaneous/Collapse';
import classes from './Alert.module.css';

const TypeStylesProps = {
  'info-gray': {
    icon: <InfoIcon />,
  },
  'info-blue': {
    icon: <InfoIcon />,
  },
  error: {
    icon: <IconWarning />,
  },
  warning: {
    icon: <IconWarning />,
  },
};

interface AlertProps extends Omit<MantineAlertProps, 'children'> {
  type?: keyof typeof TypeStylesProps;
  children?: React.ReactNode;
}

const Alert = (props: AlertProps) => {
  const typeStylesProps = useMemo(
    () => (props.type ? TypeStylesProps[props.type] : {}),
    [props.type]
  );

  return (
    <MantineAlert {...typeStylesProps} {...props}>
      {props.children}
    </MantineAlert>
  );
};

const AlertDefaultProps = {
  radius: 'sm',
  variant: 'light',
  type: 'info-blue',
};

const AlertDefaults = MantineAlert.extend({
  defaultProps: AlertDefaultProps,
  classNames: {
    root: classes.root,
    body: classes.body,
    icon: classes.icon,
    title: classes.title,
    message: classes.message,
    closeButton: classes.closeButton,
  },
});

interface AlertTransitionProps extends Pick<AlertProps, 'type' | 'variant' | 'title' | 'icon'> {
  children?: React.ReactNode;
  transitionProps?: Partial<Omit<TransitionProps, 'mounted'>>;
  mounted: boolean;
}
/** @deprecated - Use Alert.Collapse */
const AlertTransition = ({
  variant,
  type,
  title,
  children,
  mounted,
  transitionProps,
  ...rest
}: AlertTransitionProps) => {
  return (
    <Transition mounted={mounted} transition="pop" duration={150} {...transitionProps}>
      {(styles) => (
        <Box style={styles}>
          <Alert title={title} variant={variant} type={type} {...rest}>
            {children}
          </Alert>
        </Box>
      )}
    </Transition>
  );
};

interface AlertCollapseProps extends AlertProps {
  children?: React.ReactNode;
  collapseProps?: Partial<Omit<CollapseProps, 'in'>>;
  in: boolean;
}
const AlertCollapse = ({
  variant,
  type,
  title,
  children,
  collapseProps,
  in: opened,
  ...rest
}: AlertCollapseProps) => {
  return (
    <Collapse in={opened} {...collapseProps}>
      <Alert title={title} variant={variant} type={type} {...rest}>
        {children}
      </Alert>
    </Collapse>
  );
};

/**
 * @deprecated - Use Alert.Collapse
 *  */
Alert.Transition = AlertTransition;
Alert.Collapse = AlertCollapse;
export { Alert, AlertDefaults };
export type { AlertProps };
